
import {
  defineComponent, ref, inject, computed, ComputedRef,
} from 'vue';
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import Tooltip from 'primevue/tooltip';
import SpmTableAPI from '@/components/table-rest-api/SpmTableAPI.vue';
import SpmDateDisplay from '@/components/table-rest-api/SpmDateDisplay.vue';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import { NestApiType } from '@/composables/nestApi';
import type {
  SpmColumnConfig,
  ApiCallConfig,
  SpmApiFilter,
  ExportPayload,
} from '@/components/table-rest-api/types';
import { showToastError } from '@/helpers';

export default defineComponent({
  name: 'CustomerGroupsV2',
  components: {
    PageHeader,
    SpmTableAPI,
    SpmDateDisplay,
    SpmBadge,
  },

  directives: {
    tooltip: Tooltip,
  },

  setup() {
    const { t } = useI18n();

    const globalSourceFilters = inject<ComputedRef<SpmApiFilter[]>>('globalSourceFilters', computed(() => []));
    const persistentFilters = computed(() => [...globalSourceFilters.value]);

    // API call configuration
    const apiCall = ref<ApiCallConfig | null>({
      type_api: NestApiType.CLIENT_API,
      path: '/customers-groups',
      secure: true,
    });

    const columns: SpmColumnConfig[] = [
      {
        id: 'id',
        header: '',
        fields: ['id'],
        hidden: true,
      },
      {
        id: 'group_id',
        header: '',
        fields: [{
          name: 'group_id',
          label: t('myShopData.customer_groups.headers.id_group'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],

        hidden: true,
      },
      {
        id: 'group_info',
        header: t('myShopData.customer_groups.headers.name'),
        fields: [{
          name: 'name',
          label: t('myShopData.customer_groups.headers.name'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }, {
          name: 'created_at',
          label: t('myShopData.customer_groups.headers.date_creation'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }, {
          name: 'updated_at',
          label: t('myShopData.customer_groups.headers.date_modification'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'dateRange',
          },
        }],
      },
      {
        id: 'lang',
        header: t('myShopData.customer_groups.headers.lang'),
        fields: [{
          name: 'lang',
          label: t('myShopData.customer_groups.headers.lang'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'textInput',
          },
        }],
      },
      {
        id: 'active',
        header: t('myShopData.customer_groups.headers.active'),
        fields: [{
          name: 'is_active',
          label: t('myShopData.customer_groups.headers.active'),
          sortable: true,
          filterable: true,
          filterConfig: {
            type: 'multiSelect',
            options: [
              { value: 1, label: t('active') },
              { value: 0, label: t('inactive') },
            ],
          },
        }],
      },
    ];

    // Helper function to clean strings
    const cleanStr = (value: string | number | null | undefined): string => {
      if (value === null || value === undefined || value === '') {
        return '';
      }
      return String(value).trim();
    };

    /**
     * Handle export action from SpmTableAPI
     */
    const handleExport = async (payload: ExportPayload) => {
      try {
        // Build export request payload - already simplified from SpmTableAPI
        const exportRequest: Record<string, unknown> = {
          entity_type: payload.exportConfig.entityType,
          select_all_mode: payload.selectAllMode,
          selected_ids: payload.selectedIds,
          total_count: payload.totalCount,
          ...payload.exportConfig.additionalParams,
        };

        // Add filters only if present (selectAllMode from selection, not "Export all" button)
        if (payload.filters) {
          exportRequest.filters = payload.filters;
        }

        console.log('Export request:', exportRequest);
        // TODO : Call export API
        // const response = await nestCall(
        //   'post',
        //   NestApiType.CLIENT_API,
        //   '/exports/customer-groups',
        //   exportRequest,
        //   '',
        //   undefined,
        //   true,
        // );

        // if (response && !response.error) {
        //   await showToastSuccess(t('myShopData.customer_groups.export.success'));
        // } else {
        //   await showToastError(t('myShopData.customer_groups.export.error'));
        // }
      } catch (error) {
        console.error('Export error:', error);
        await showToastError(t('myShopData.customer_groups.export.error'));
      }
    };

    return {
      t,
      apiCall,
      columns,
      persistentFilters,
      cleanStr,
      handleExport,
    };
  },
});
